import { reactive, computed, nextTick, watch, onMounted, onBeforeUnmount, toRefs } from 'vue';
import { useStore } from 'vuex';
import * as Annotations from "../Components/Call/Annotations.js";
import { buildConferenceHubAsync } from "../API/conference.hub.js";
import DocumentSigningStatuses from "../models/documentSigningStatuses.js";
import * as OT from "@opentok/client";
import { joinSession } from "../API/video-session.api.js";
import { FormFields } from '@syncfusion/ej2-pdfviewer';

let OpenTokSession;
let OpenTokSelfStream;
let OpenTokClientStream;
let OpenTokSessionPublisher;

export function useCallScreen(onEndCallMethod = undefined) {
	const store = useStore();

	const onEndCall = (callStatus) => {
		if (onEndCallMethod) {
			onEndCallMethod(callStatus);
		} else {
			return;
		}
	};

	//data property
	//moved isSignatureModalShown, isInitialsModalShown from Conference.vue data
	const callScreenVars = reactive({
		selectedDocumentId: 0,
		isPanMode: true,
		pdfViewer: {},
		verticalOffset: 0,
		formFieldsMock: null,
		currentTask: {},
		callStarted: false,
		signingStatusUpdates: {},
		pdfViewerMouseDown: false,
		clientDisconnectedTimerRef: null,
		notaryDisconnectedTimerRef: null,
		clientDisconnectionSeconds: null,
		notaryDisconnectionSeconds: null,
		deviceCheckConfig: {},
		openTokSessionReady: false,
		openTokSelfStreamReady: false,
		openTokClientStreamReady: false,
		openTokSessionId: ``,
		openTokSubcribeTasksQueue: [],
		openTokSubscribeTasksCount: 0,
		isMuted: false,
		notaryName: ``,
		hubError: null,
		isReloading: false,
		hub: null,
		isSignatureModalShown: false,
		isInitialsModalShown: false,
		expandVid: false,
		expandID: false
	});

	//computed data:
	const pdfViewerComputed = computed(() => callScreenVars.pdfViewer);
	const currentTaskComputed = computed(() => callScreenVars.currentTask);
	const pdfViewerMouseDownComputed = computed(() => callScreenVars.pdfViewerMouseDown);
	const selectedDocumentIdComputed = computed(() => callScreenVars.selectedDocumentId);
	const verticalOffsetComputed = computed(() => callScreenVars.verticalOffset);
	const openTokSessionReadyComputed = computed(() => callScreenVars.openTokSessionReady);
	const notaryNameComputed = computed(() => callScreenVars.notaryName);
	const openTokSelfStreamReadyComputed = computed(() => callScreenVars.openTokSelfStreamReady);
	const openTokClientStreamReadyComputed = computed(() => callScreenVars.openTokClientStreamReady);
	const isMutedComputed = computed(() => callScreenVars.isMuted);
	const expandVidComputed = computed(() => callScreenVars.expandVid);
	const expandIDComputed = computed(() => callScreenVars.expandID);

	//computed VueX properties
	const author = computed(() => store.state.author);
	const notarizationDocuments = computed(() => store.state.notarizationDocuments);
	const notarizationId = computed(() => store.state.API.notarizationId);

	//computed properties - moved largeScreen to Conference.vue computed
	const notaryAnnotations = computed(() => callScreenVars.pdfViewer?.annotationCollection?.filter(t => !t.customData?.isTask));
	const tasks = computed(() => callScreenVars.pdfViewer?.annotationCollection?.filter(t => t.customData?.isTask));
	const completedTasks = computed(() => tasks?.value?.filter(t => t.customData?.isComplete));
	const seals = computed(() => notaryAnnotations?.value?.filter(t => t.customData?.isSeal));
	const callDocuments = computed(() => {
		return notarizationDocuments.value.map((d) => {
			const statusUpdate = callScreenVars.signingStatusUpdates[d.id];
			if (statusUpdate)
				d.signingStatusId = statusUpdate;
			return {
				...d,
				isComplete: d.signingStatusId === DocumentSigningStatuses.APPROVED,
				isDeclined: d.signingStatusId === DocumentSigningStatuses.REJECTED,
				isReadonly: d.signingStatusId !== DocumentSigningStatuses.NOT_STARTED
			};
		});
	});
	const selectedDocument = computed(() => callDocuments?.value.find(d => d.id === callScreenVars.selectedDocumentId) ?? {});
	const selectedDocumentIsReadOnly = computed(() => selectedDocument?.value.isReadonly);
	const isClientDisconnected = computed(() => callScreenVars.clientDisconnectionSeconds != null);
	const isNotaryDisconnected = computed(() => callScreenVars.notaryDisconnectionSeconds != null);

	//Watches - do not need to return outside of composable
	watch(() => callScreenVars.pdfViewer, (newVal) => {
		setPdfViewer(newVal);
		resizeViewer();
		window.addEventListener(`resize`, resizeViewer);
	});
	watch(() => callScreenVars.selectedDocumentId, async (newVal) => {
		await documentIdChanged(newVal);
		if (author.value === Annotations.Authors.Notary) loadNotarialActs();
	});
	watch(selectedDocumentIsReadOnly, (newVal) => {
		if (newVal) {
			resetActivePage();
		}
	});

	onMounted(async () => {
		const hubConfig = {
			logLevel: window.Notary.Config.Hubs.LogLevel,
			onNotaryPing: async () => {
				await callScreenVars.hub.invoke(`LawDepotPingBack`);
				callScreenVars.notaryDisconnectionSeconds = null;
				clearInterval(callScreenVars.notaryDisconnectedTimerRef);
			},
			onLawDepotPingBack: async () => {
				await callScreenVars.hub.invoke(`StartCall`);
				await callScreenVars.hub.invoke(`DocumentStatusUpdated`, JSON.stringify(callScreenVars.signingStatusUpdates));
				callScreenVars.clientDisconnectionSeconds = null;
				callScreenVars.callStarted = true;
				callScreenVars.hubError = null;
				clearInterval(callScreenVars.clientDisconnectedTimerRef);
			},
			onStartCall: async (notaryFirstName, notaryLastName) => {
				callScreenVars.notaryName = `${notaryFirstName} ${notaryLastName}`;
				callScreenVars.callStarted = true;
				callScreenVars.hubError = null;
			},
			// Triggered when client recieves Session Id from Notary
			onSessionId: async (sessionId) => {
				await openTokSession(sessionId);
			},
			onDocumentStatusUpdated: async (signingStatusUpdates) => { callScreenVars.signingStatusUpdates = JSON.parse(signingStatusUpdates); },
			onEndCall: async (endCall) => { onEndCall(endCall); },
			onReloadDocument: async (documentId) => { if (`${callScreenVars.selectedDocumentId}` === documentId) await reloadDocument(); },
			onCloseError: () => {
				//attempt to reconnect after 5 seconds
				setTimeout(async () => { await startHub(); }, 5000);
			},
			onClientDisconnected: async () => {
				callScreenVars.callStarted = false;
				callScreenVars.clientDisconnectionSeconds = window.Notary.Config.Conference.DisconnectionSeconds;
				callScreenVars.clientDisconnectedTimerRef = setInterval(() => { if (callScreenVars.clientDisconnectionSeconds > 0) callScreenVars.clientDisconnectionSeconds--; }, 1000);
			},
			onNotaryDisconnected: async () => {
				callScreenVars.callStarted = false;
				callScreenVars.notaryDisconnectionSeconds = window.Notary.Config.Conference.DisconnectionSeconds;
				callScreenVars.notaryDisconnectedTimerRef = setInterval(() => { if (callScreenVars.notaryDisconnectionSeconds > 0) callScreenVars.notaryDisconnectionSeconds--; }, 1000);
			}
		};
		callScreenVars.hub = await buildConferenceHubAsync(hubConfig, notarizationId.value);
		await startHub();
		conferenceKeepAlive();
	});

	onBeforeUnmount(() => {
		window.removeEventListener(`resize`, resizeViewer);
	});

	//VueX MapState Methods
	const syncAnnotationUpdate = (payload) => {
		store.dispatch(`AnnotationsSync/syncAnnotationUpdate`, payload);
	};
	const syncAnnotationAdd = (payload) => {
		store.dispatch(`AnnotationsSync/syncAnnotationAdd`, payload);
	};
	const syncAnnotationRemove = (payload) => {
		store.dispatch(`AnnotationsSync/syncAnnotationRemove`, payload);
	};
	const receiveUpdate = (payload) => {
		store.dispatch(`AnnotationsSync/receiveUpdate`, payload);
	};
	const documentIdChanged = async (payload) => {
		await store.dispatch(`AnnotationsSync/documentIdChanged`, payload);
	};
	const resetActivePage = () => {
		store.dispatch(`AnnotationsSync/resetActivePage`);
	};
	const stopAnnotationsSyncHub = () => {
		store.dispatch(`AnnotationsSync/stopHub`);
	};
	const loadDocuments = () => {
		return store.dispatch(`loadDocuments`);
	};
	const loadNotarialActs = () => {
		return store.dispatch(`loadNotarialActs`);
	};
	const loadSigners = () => {
		return store.dispatch(`Signers/loadSigners`);
	};
	const loadSignerTypes = () => {
		return store.dispatch(`Signers/loadSignerTypes`);
	};
	const setPdfViewer = (pdfViewer) => {
		store.commit(`AnnotationsSync/setPdfViewer`, pdfViewer);
	};

	//methods
	const startHub = async () => {
		await callScreenVars.hub.start().catch((error) => { callScreenVars.hubError = error; });
	};
	const openTokSession = async (sessionId) => {
		if (OpenTokSession) {	
			// Session already connected, no need to reconnect
			return;
		}
		callScreenVars.deviceCheckConfig = JSON.parse(sessionStorage[`DeviceCheckConfig`]);
		const sessionInfo = await joinSession(sessionId);
		OpenTokSession = OT.initSession(sessionInfo.apiKey, sessionId);
		OpenTokSession.on(`connectionCreated`, () => {
			callScreenVars.openTokSessionReady = true;
		});
		OpenTokSession.on(`streamCreated`, (streamCreatedEvent) => {
			OpenTokClientStream = streamCreatedEvent.stream;
			callScreenVars.openTokClientStreamReady = true;
		});
		OpenTokSession.on(`streamDestroyed`, () => {
			OpenTokClientStream = null;
			callScreenVars.openTokClientStreamReady = false;
			callScreenVars.openTokSubcribeTasksQueue = [];
		});
		OpenTokSession.connect(sessionInfo.token);
	};
	const getAnnotationById = (annotationId) => {
		return callScreenVars.pdfViewer.annotationCollection.find((a) => a.annotationId == annotationId);
	};
	const getSelectedAnnotationId = () => {
		return callScreenVars.pdfViewer.selectedItems?.annotations[0]?.annotName;
	};
	const selectAnnotation = (annotation) => {
		callScreenVars.pdfViewer.viewerBase
			.getTool(`Select`)
			.commandHandler.select(
				[annotation.uniqueKey],
				annotation.annotationSelectorSettings
			);
		const annotationId = annotation.annotationId;
		callScreenVars.pdfViewer.trigger(`annotationSelect`, { name: `annotationSelect`, annotationId, pageIndex: annotation.pageNumber, annotation });
	};
	const showSignatureModal = (task) => {
		callScreenVars.currentTask = task;
		callScreenVars.isSignatureModalShown = true;
	};
	const showInitialsModal = (task) => {
		callScreenVars.currentTask = task;
		callScreenVars.isInitialsModalShown = true;
	};
	const completeTask = async (task, annotation) => {
		const viewer = callScreenVars.pdfViewer;
		viewer.annotationModule.deleteAnnotation();
		await new Promise((resolve) => {
			let n = 3;
			const wait = () => {
				const toDelete = getAnnotationById(task.annotationId);
				if (!toDelete) {
					resolve();
				} else if (n > 0) {						
					n--;
					window.setTimeout(wait, 100);
				} else {
					throw new Error(`Couldn't delete task: ${task}`);
				}
			};
			window.setTimeout(wait, 100);
		});
		syncAnnotationRemove(task.annotationId);
		viewer.addAnnotation(annotation);
		let n = 10;
		const retry = () => {
			const added = getAnnotationById(annotation.annotationId);
			// image annotations take some time to be ready
			if (added?.uniqueKey) {
				syncAnnotationAdd(added);
				selectAnnotation(added);
			} else if (n > 0) {
				n--;
				window.setTimeout(retry, 100);
			} else {
				console.error(`Couldn't sync completed task.`);
			}
		};
		retry();
	};
	const completeTypeOrDrawTask = async (task, annotation) => {
		const taskAnnotation = getAnnotationById(task.annotationId);
		selectAnnotation(taskAnnotation);
		await completeTask(task, annotation);
	};
	const updateSignatureAspectRatio = (pathData) => {
		if(callScreenVars.formFieldsMock == null) callScreenVars.formFieldsMock = new FormFields(callScreenVars.pdfViewer, callScreenVars.pdfViewer.viewerBase);
		return callScreenVars.formFieldsMock.updateSignatureAspectRatio(pathData, true);
	};
	const completeDrawTask = async () => {
		const viewer = callScreenVars.pdfViewer;
		const pathData = viewer.viewerBase.signatureModule.outputString;
		const signatureBounds = updateSignatureAspectRatio(pathData);
		const task = callScreenVars.currentTask;
		await completeTypeOrDrawTask(task, new Annotations.Ink(task, author.value, pathData, signatureBounds));
	};
	const completeTypeTask = async (inputValue, fontFamily, fontSize) => {
		const task = callScreenVars.currentTask;
		await completeTypeOrDrawTask(task, new Annotations.SignatureText(task, author.value, inputValue, fontFamily, fontSize));
	};
	const callStartedCommon = async () => {
		await Promise.all([loadDocuments(), loadSigners(), loadSignerTypes()]);
		callScreenVars.selectedDocumentId = notarizationDocuments.value[0].id;
	};
	const resizeViewer = () => {
		const top = callScreenVars.pdfViewer?.getRootElement?.()?.getBoundingClientRect?.()?.y;
		if (!top) return;
		const height = window.innerHeight;
		const heightStyle = `${height - top}px`;
		callScreenVars.pdfViewer.height = heightStyle;

		//The following is to account for us moving the sidebar element.  When we set pdfViewer.height it will also set the 
		//height of the sidebar element which causes it to be longer than the screen thus a scroll bar appears
		//the following corrects that by setting a maxheight on the parent `pagination` element and setting the sidebar height to auto
		const pagination = document.getElementById(`pagination`);
		pagination.style.maxHeight = heightStyle;

		const ej2Sidebar = document.getElementById(`pdfviewer_sideBarContentContainer`);
		ej2Sidebar.style.height = `auto`;
	};
	const reloadDocument = async () => {
		callScreenVars.isReloading = true;
		if (author.value === Annotations.Authors.Notary) {
			callScreenVars.hub.invoke(`ReloadDocument`, `${callScreenVars.selectedDocumentId}`);
			loadNotarialActs();
		}
		callScreenVars.verticalOffset = callScreenVars.pdfViewer.element.querySelector(`#pdfviewer_viewerContainer`).scrollTop;
		await stopAnnotationsSyncHub();
		callScreenVars.pdfViewer.load(`${callScreenVars.selectedDocumentId}`);
		callScreenVars.isReloading = false;
	};
	const conferenceKeepAlive = () => {
		if (callScreenVars.hub.state == `Connected`) {
			callScreenVars.hub.invoke(`KeepAlive`);
		}

		setTimeout(conferenceKeepAlive, 30 * 1000);
	};
	const publishVideoStream = (elementRef) => {
		// To prevent the doubling of published streams check to see if we already have a publisher object.
		if (OpenTokSessionPublisher == null) {
			let publisherProperties = {
				insertMode: `append`,
				videoSource: callScreenVars.deviceCheckConfig.camera,
				audioSource: callScreenVars.deviceCheckConfig.microphone,
				width: `100%`
			};
			OpenTokSessionPublisher = OT.initPublisher(elementRef, publisherProperties);
			OpenTokSessionPublisher.on(`streamCreated`, (streamCreatedEvent) => {
				OpenTokSelfStream = streamCreatedEvent.stream;
				callScreenVars.openTokSelfStreamReady = true;
			});
			OpenTokSession.publish(OpenTokSessionPublisher);
			OpenTokSessionPublisher.publishAudio(!callScreenVars.isMuted);
		}
	};
	const runOpenTokSubscribeTasks = async () => {
		const tasks = callScreenVars.openTokSubcribeTasksQueue;
		callScreenVars.openTokSubcribeTasksQueue = [];
		for(let task of tasks) await task();
	};
	const subscribe = async (stream, elementRef, subscribeToAudio = false, addToQueue = false) => {
		if (!stream) {
			return;
		}
		let subscriberProperties = {
			insertMode: `append`,
			videoSource: callScreenVars.deviceCheckConfig.camera,
			width: `100%`,
			subscribeToAudio,
			subscribeToVideo: !subscribeToAudio,
			showControls: false
		};

		const task = () => new Promise((resolve) => OpenTokSession.subscribe(stream, elementRef, subscriberProperties, resolve));
		if(addToQueue) {
			if(subscribeToAudio) {
				callScreenVars.openTokSubcribeTasksQueue.push(task);
			} else {
				callScreenVars.openTokSubcribeTasksQueue.unshift(task);
			}
			await nextTick();
			if(callScreenVars.openTokSubcribeTasksQueue.length === callScreenVars.openTokSubscribeTasksCount) runOpenTokSubscribeTasks();			
		} else task();
	};
	const subscribeSelfStream = (elementRef) => {
		return subscribe(OpenTokSelfStream, elementRef, false);
	};
	const subscribeClientStream = (elementRef, subscribeToAudio = false) => {
		return subscribe(OpenTokClientStream, elementRef, subscribeToAudio, true);
	};
	const changeSubscribeTasksCount = (count = 1) => {
		callScreenVars.openTokSubscribeTasksCount += count;
	};
	const toggleMute = () => {
		callScreenVars.isMuted = !callScreenVars.isMuted;
		if (OpenTokSessionPublisher) {
			OpenTokSessionPublisher.publishAudio(!callScreenVars.isMuted);
		}
	};
	const renderedAndImportedAnnotationsCount = () => {
		const renderedAnnotationsIds = callScreenVars.pdfViewer?.annotations?.map?.(a => a.annotName) ?? [];
		const importedAnnotationsIds = callScreenVars.pdfViewer.viewerBase?.documentAnnotationCollections?.flatMap?.(Object.values)?.flatMap?.(Object.values)?.map?.(a => a.AnnotName) ?? [];
		// Return unique ids count
		return renderedAnnotationsIds.concat(importedAnnotationsIds).filter((annot, idx, arr) => idx === arr.indexOf(annot)).length;
	};
	const exportAnnotations = () => {
		return callScreenVars.pdfViewer?.viewerBase?.constructJsonDownload?.();
	};
	const exportedAnnotationsCount = () => {
		const keys = [`freeTextAnnotation`, `inkSignatureData`, `measureShapeAnnotations`, `shapeAnnotations`,
			`signatureData`, `stampAnnotations`, `stickyNotesAnnotation`, `textMarkupAnnotations`];
		return Object.entries(exportAnnotations() ?? {}).filter((entry) => keys.includes(entry[0])).flatMap((entry) => JSON.parse(entry[1])).reduce((acc, x) => x.length + acc, 0);
	};
	const annotationsCountsAreEqual = () => {
		const annotationsCount = callScreenVars.pdfViewer?.annotationCollection?.length ?? 0;
		return annotationsCount === renderedAndImportedAnnotationsCount() && annotationsCount === exportedAnnotationsCount();
	};
	const toggleVideo = () => {
		callScreenVars.expandVid = !callScreenVars.expandVid;
	};
	const toggleID = () => {
		callScreenVars.expandID = !callScreenVars.expandID;
	};

	return {
		...toRefs(callScreenVars),
		//computed
		author,
		notarizationDocuments,
		notarizationId,
		notaryAnnotations,
		tasks,
		completedTasks,
		seals,
		callDocuments,
		selectedDocument,
		selectedDocumentIsReadOnly,
		isClientDisconnected,
		isNotaryDisconnected,
		pdfViewerComputed,
		currentTaskComputed,
		pdfViewerMouseDownComputed,
		selectedDocumentIdComputed,
		verticalOffsetComputed,
		openTokSessionReadyComputed,
		notaryNameComputed,
		openTokSelfStreamReadyComputed,
		openTokClientStreamReadyComputed,
		isMutedComputed,
		expandIDComputed,
		expandVidComputed,
		//methods
		syncAnnotationUpdate,
		syncAnnotationAdd,
		syncAnnotationRemove,
		receiveUpdate,
		documentIdChanged,
		resetActivePage,
		stopAnnotationsSyncHub,
		loadDocuments,
		loadNotarialActs,
		loadSigners,
		loadSignerTypes,
		setPdfViewer,
		startHub,
		openTokSession,
		getAnnotationById,
		getSelectedAnnotationId,
		selectAnnotation,
		showSignatureModal,
		showInitialsModal,
		completeTask,
		completeTypeOrDrawTask,
		updateSignatureAspectRatio,
		completeDrawTask,
		completeTypeTask,
		callStartedCommon,
		resizeViewer,
		reloadDocument,
		conferenceKeepAlive,
		publishVideoStream,
		runOpenTokSubscribeTasks,
		subscribe,
		subscribeSelfStream,
		subscribeClientStream,
		changeSubscribeTasksCount,
		toggleMute,
		renderedAndImportedAnnotationsCount,
		exportAnnotations,
		exportedAnnotationsCount,
		annotationsCountsAreEqual,
		toggleVideo,
		toggleID
	};
}

